import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link, useNavigate, useParams } from 'react-router';
import { useDialogs } from '../../hooks/useDialogs/useDialogs';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Product } from '../../models/Product';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { GET_PRODUCT_BY_ID_QUERY, GET_PRODUCTS_QUERY } from '../../queries/ProductQueries';
import PageContainer from '../layout/PageContainer';
import { DELETE_PRODUCT_BY_ID_MUTATION } from '../../mutations/ProductMutations';
import { AttachMoneyOutlined, BlenderOutlined, InventoryOutlined } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { ProductItem } from "../../models/ProductItem";
import { toUSCurrency } from '../../utilities/CurrencyUtilities';
import { Batch } from '../../models/Batch';
import dayjs from 'dayjs';
import { PackagingType } from '../../models/ProductInventoryItem';
import { arrayToMap } from '../../utilities/MapUtilities';

export default function ProductShow() {
  const { productId } = useParams();
  const navigate = useNavigate();

  const dialogs = useDialogs();
  const notifications = useNotifications();

  const [getProductById] = useLazyQuery(
    GET_PRODUCT_BY_ID_QUERY, {
    fetchPolicy: 'cache-and-network'
  });

  const [deleteProductById] = useMutation(
    DELETE_PRODUCT_BY_ID_MUTATION, {
    refetchQueries: [{ query: GET_PRODUCTS_QUERY }],
  });

  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(productId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getProductByIdError } = await getProductById({ variables: { id } });

        setIsLoading(false);
        setError(null);

        if (getProductByIdError) {
          setError(getProductByIdError);
        }

        if (data?.product) {
          setProduct(data.product);
        } else {
          setError({ message: 'No product found' } as Error);
        }
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getProductById, productId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = useCallback(() => {
    navigate(`/dashboard/products/${productId}/edit`);
  }, [navigate, productId]);

  const handleCreateBatch = useCallback(() => {
    navigate(`/dashboard/batches/${productId}/create`);
  }, [navigate, productId]);

  const handleDelete = useCallback(async () => {
    if (!product) {
      return;
    }

    const confirmed = await dialogs.confirm(
      `Do you wish to delete ${product.name}?`,
      {
        title: `Delete product?`,
        severity: 'error',
        okText: 'Delete',
        cancelText: 'Cancel',
      },
    );

    if (confirmed) {
      setIsLoading(true);
      try {
        const { data: result } = await deleteProductById({ variables: { id: product.id } });

        if (result?.deleted) {
          navigate('/dashboard/products');

          notifications.show('Product deleted successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });
        } else {
          notifications.show(
            'Failed to delete product. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (deleteError) {
        notifications.show(
          `Failed to delete product. Reason:' ${(deleteError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
      setIsLoading(false);
    }
  }, [product, dialogs, deleteProductById, navigate, notifications]);

  const handleBack = useCallback(() => {
    navigate('/dashboard/products');
  }, [navigate]);

  const inventoryQuantityOnHand = useMemo(() => {
    return arrayToMap<PackagingType, number>(
      (product?.totalQuantityOnHand || [])
    );
  }, [product?.totalQuantityOnHand]);

  const renderShow = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return product ? (
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="product-details-panel"
            id="product-details-panel"
          >
            <Typography component="span" sx={{ fontSize: '16px' }}>
              <strong>Details</strong>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2} sx={{ width: '100%' }}>
              <Grid size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">Name</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {product.name}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">SKU</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {product.sku || 'N/A'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">Customer</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {product.customer?.name || 'N/A'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">Costing Type</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {product.costingType}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">Matching Product</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {product.matchingProduct?.name || 'N/A'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">Total Item Qty</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {product.totalItemQuantity?.toFixed(2) || '0.00'} lbs
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, sm: 6, md: 6, lg: 4 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">Total Average Item Cost per Batch Pound</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    ${toUSCurrency(product.totalAverageItemCostPerBatchPound ?? 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, sm: 6, md: 6, lg: 4 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">Total Average Item Cost per Standard Batch (50#)</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    ${toUSCurrency(product.totalAverageItemCostPerStandardBatch ?? 0)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="ingredient-items-panel"
            id="ingredient-items-panel"
          >
            <Typography component="span" sx={{ fontSize: '16px' }}>
              <strong>Ingredient Items</strong>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer sx={{ maxHeight: '200px' }}>
              <Table stickyHeader sx={{ minWidth: 600 }} aria-label="dense table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row">Name</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row">Notes</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} component="th" scope="row" align="right">Average Item Cost per Pound</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} component="th" scope="row" align="right">Average Item Cost per Batch Pound</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} component="th" scope="row" align="right">Average Item Cost per Standard Batch (50#)</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '10%' }} component="th" scope="row" align="right">Quantity (lbs)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(product.ingredientItems || []).map((item: ProductItem) => (
                    <TableRow
                      key={item.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }}>
                        <a href={`/dashboard/items/${item.item?.id}`}>{item.item?.name}</a>
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }}>
                        {item.notes}
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} align="right">
                        ${toUSCurrency(item.item?.averageCostPerUnit ?? 0)} /lb
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} align="right">
                        ${toUSCurrency(item.averageItemCostPerBatchPound ?? 0)} /lb
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} align="right">
                        ${toUSCurrency(item.averageItemCostPerStandardBatch ?? 0)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '10%' }} align="right">
                        {item.quantity.toFixed(3)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="packaging-items-panel"
            id="packaging-items-panel"
          >
            <Typography component="span" sx={{ fontSize: '16px' }}>
              <strong>Packaging Items</strong>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer sx={{ maxHeight: '250px' }}>
              <Table stickyHeader sx={{ minWidth: 600 }} aria-label="dense table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }} component="th" scope="row">Name</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }} component="th" scope="row">Notes</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} component="th" scope="row" align="right">Average Item Cost per Standard Batch (50#)</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} component="th" scope="row" align="right">Quantity (lbs)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(product.packagingItems || []).map((item: ProductItem) => (
                    <TableRow
                      key={item.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }} component="th" scope="row">
                        <a href={`/dashboard/items/${item.item?.id}`}>{item.item?.name}</a>
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }}>
                        {item.notes}
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} align="right">
                        ${toUSCurrency(item.averageItemCostPerStandardBatch ?? 0)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} align="right">
                        {item.quantity.toFixed(3)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }} component="th" scope="row">
                    </TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '30%' }}>
                    </TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} align="right">
                    </TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} align="right">
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="completed-batches-panel"
            id="completed-batches-panel"
          >
            <Typography component="span" sx={{ fontSize: '16px' }}>
              <strong>Completed Batches</strong>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer sx={{ maxHeight: '250px' }}>
              <Table sx={{ minWidth: 600 }} aria-label="dense table" size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '25%' }} component="th" scope="row">Lot Number</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '25%' }} component="th" scope="row">Created</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '25%' }} component="th" scope="row" align="right">Qty (lbs)</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '25%' }} component="th" scope="row" align="right">Total Cost ($)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(product.batches || []).map((batch: Batch) => (
                    <TableRow
                      key={batch.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '25%' }}>
                        <a href={`/dashboard/batches/${batch.id}`}>{batch.lotNumber}</a>
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '25%' }}>
                        {dayjs(batch.dateTimeCreated).format('M/D/YYYY h:mm A')}
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '25%' }} align="right">
                        {batch.amount.toFixed(3)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '25%' }} align="right">
                        {toUSCurrency(batch.totalCost)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="inventory-panel"
            id="inventory-panel"
          >
            <Typography component="span" sx={{ fontSize: '16px' }}>
              <strong>Inventory</strong>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer sx={{ maxHeight: '250px' }}>
              <Table sx={{ minWidth: 600 }} aria-label="dense table" size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} component="th" scope="row">Type</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} component="th" scope="row" align="right">QOH</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="left">
                      Jars
                    </TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="right">
                      {inventoryQuantityOnHand.get(PackagingType.JAR)?.toFixed(0) || 0}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="left">
                      Pouches
                    </TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="right">
                      {inventoryQuantityOnHand.get(PackagingType.POUCH)?.toFixed(0) || 0}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="left">
                      Bulk
                    </TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="right">
                      {inventoryQuantityOnHand.get(PackagingType.BULK)?.toFixed(2) || 0} lbs
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="actions-panel"
            id="actions-panel"
          >
            <Typography component="span" sx={{ fontSize: '16px' }}>
              <strong>Actions</strong>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
              >
                Back
              </Button>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
                {product?.hasInventory && (
                  <Button
                    variant="contained"
                    startIcon={<InventoryOutlined />}
                    component={Link}
                    to={`/dashboard/products/${productId}/product-inventory-items`}
                  >
                    Inventory
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<AttachMoneyOutlined />}
                  component={Link}
                  to={`/dashboard/products/${productId}/costing`}
                >
                  Costing
                </Button>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<BlenderOutlined />}
                  onClick={() => handleCreateBatch()}
                >
                  Create Batch
                </Button>
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>
    ) : null;
  }, [isLoading, error, product, inventoryQuantityOnHand, handleBack, handleDelete, productId, handleEdit, handleCreateBatch]);

  const pageTitle = `${product?.name}`;

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Products', path: '/dashboard/products' },
        { title: pageTitle },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>{renderShow}</Box>
    </PageContainer>
  );
}
